import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import ConsultationBookingForm from '../contact-multi-channel-engagement-hub/components/ConsultationBookingForm';
import Icon from '../../components/AppIcon';

const ScheduleConsultation = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Schedule Consultation | Northstar Analytics</title>
        <meta 
          name="description" 
          content="Book a strategic consultation with Northstar Analytics data strategy experts. Available Monday slots for personalized data transformation guidance." 
        />
        <meta name="keywords" content="book consultation, data strategy consultation, schedule meeting, Dublin analytics experts" />
        <meta property="og:title" content="Schedule Consultation | Northstar Analytics" />
        <meta property="og:description" content="Book your strategic consultation with our data strategy experts. Monday slots available." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/schedule-consultation" />
      </Helmet>
      <Header />
      <main className="pt-16 lg:pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/5 via-secondary/5 to-background py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary/10 rounded-full mb-6">
              <Icon name="Calendar" size={32} className="text-secondary" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-primary mb-6">
              Schedule Your Strategic Consultation
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Book a 45-minute discovery session with our data strategy experts. We'll assess your current state, discuss strategic roadmaps, and provide actionable recommendations.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={20} className="text-secondary" />
                <span>45-minute session</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Calendar" size={20} className="text-secondary" />
                <span>Monday slots available</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Video" size={20} className="text-secondary" />
                <span>Virtual or in-person</span>
              </div>
            </div>
          </div>
        </section>

        {/* What to Expect Section */}
        <section className="py-12 bg-surface border-b border-border">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-semibold text-primary mb-8 text-center">
              What to Expect from Your Consultation
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-card rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="Target" size={24} className="text-secondary" />
                </div>
                <h3 className="font-semibold text-primary mb-2">Current State Assessment</h3>
                <p className="text-sm text-muted-foreground">
                  We'll evaluate your existing data infrastructure and identify key opportunities.
                </p>
              </div>
              <div className="bg-card rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="Map" size={24} className="text-secondary" />
                </div>
                <h3 className="font-semibold text-primary mb-2">Strategic Roadmap</h3>
                <p className="text-sm text-muted-foreground">
                  Receive a high-level roadmap tailored to your business objectives.
                </p>
              </div>
              <div className="bg-card rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="Lightbulb" size={24} className="text-secondary" />
                </div>
                <h3 className="font-semibold text-primary mb-2">Technology Recommendations</h3>
                <p className="text-sm text-muted-foreground">
                  Get expert guidance on tools and platforms that fit your needs.
                </p>
              </div>
              <div className="bg-card rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="TrendingUp" size={24} className="text-secondary" />
                </div>
                <h3 className="font-semibold text-primary mb-2">Implementation Timeline</h3>
                <p className="text-sm text-muted-foreground">
                  Understand realistic timelines and resource requirements.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Booking Form */}
        <ConsultationBookingForm />

        {/* Trust Indicators */}
        <section className="py-12 bg-surface">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="flex flex-col items-center">
                <Icon name="Shield" size={32} className="text-secondary mb-3" />
                <h4 className="font-semibold text-primary mb-2">GDPR Compliant</h4>
                <p className="text-sm text-muted-foreground">Your data is secure and protected</p>
              </div>
              <div className="flex flex-col items-center">
                <Icon name="Clock" size={32} className="text-secondary mb-3" />
                <h4 className="font-semibold text-primary mb-2">24-Hour Response</h4>
                <p className="text-sm text-muted-foreground">Confirmation within one business day</p>
              </div>
              <div className="flex flex-col items-center">
                <Icon name="Award" size={32} className="text-secondary mb-3" />
                <h4 className="font-semibold text-primary mb-2">Expert Consultants</h4>
                <p className="text-sm text-muted-foreground">15+ years combined experience</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ScheduleConsultation;